import { logger } from '../utils/logger';

/**
 * Pip's Guild Quest Service
 * Database operations and core game logic for Pip's Guild Quest minigame
 */

import { supabase } from './supabase';
import {
  GuildQuest,
  QuestTemplate,
  QuestNode,
  QuestChoice,
  QuestEnding,
  PlayerClass,
  QuestProgress,
  CreateQuestResponse,
  MakeChoiceResponse,
  PerformCombatActionResponse,
  CombatAction,
  CombatResult,
  PlayerStats,
  CombatState,
  Enemy,
  StatusEffect,
  TURN_TIMEOUT_MS,
  CLASS_INITIATIVE_BASE,
} from '../types/pipsGuildQuest';

// ============================================================================
// Quest Template Operations
// ============================================================================

/**
 * Get all available quest templates
 */
export async function getQuestTemplates(): Promise<QuestTemplate[]> {
  const { data, error } = await supabase
    .from('quest_templates')
    .select('*')
    .order('created_at', { ascending: true });

  if (error) {
    logger.error('Error fetching quest templates:', error);
    return [];
  }

  return data || [];
}

/**
 * Get a specific quest template by ID
 */
export async function getQuestTemplate(templateId: string): Promise<QuestTemplate | null> {
  const { data, error } = await supabase
    .from('quest_templates')
    .select('*')
    .eq('id', templateId)
    .single();

  if (error) {
    logger.error('Error fetching quest template:', error);
    return null;
  }

  return data;
}

/**
 * Get quest nodes for a template
 */
export async function getQuestNodes(templateId: string): Promise<QuestNode[]> {
  const { data, error } = await supabase
    .from('quest_nodes')
    .select('*')
    .eq('quest_template_id', templateId)
    .order('node_number', { ascending: true });

  if (error) {
    logger.error('Error fetching quest nodes:', error);
    return [];
  }

  return data || [];
}

/**
 * Get a specific quest node
 */
export async function getQuestNode(nodeId: string): Promise<QuestNode | null> {
  const { data, error } = await supabase
    .from('quest_nodes')
    .select('*')
    .eq('id', nodeId)
    .single();

  if (error) {
    logger.error('Error fetching quest node:', error);
    return null;
  }

  return data;
}

/**
 * Get choices for a specific node
 */
export async function getNodeChoices(nodeId: string): Promise<QuestChoice[]> {
  const { data, error } = await supabase
    .from('quest_choices')
    .select('*')
    .eq('node_id', nodeId)
    .order('choice_number', { ascending: true });

  if (error) {
    logger.error('Error fetching node choices:', error);
    return [];
  }

  return data || [];
}

/**
 * Get quest endings for a template
 */
export async function getQuestEndings(templateId: string): Promise<QuestEnding[]> {
  const { data, error } = await supabase
    .from('quest_endings')
    .select('*')
    .eq('quest_template_id', templateId)
    .order('ending_number', { ascending: true });

  if (error) {
    logger.error('Error fetching quest endings:', error);
    return [];
  }

  return data || [];
}

// ============================================================================
// Player Class Operations
// ============================================================================

/**
 * Get all player classes
 */
export async function getPlayerClasses(): Promise<PlayerClass[]> {
  const { data, error } = await supabase
    .from('player_classes')
    .select('*')
    .order('is_starter', { ascending: false });

  if (error) {
    logger.error('Error fetching player classes:', error);
    return [];
  }

  return data || [];
}

/**
 * Get starter classes only
 */
export async function getStarterClasses(): Promise<PlayerClass[]> {
  const { data, error } = await supabase
    .from('player_classes')
    .select('*')
    .eq('is_starter', true);

  if (error) {
    logger.error('Error fetching starter classes:', error);
    return [];
  }

  return data || [];
}

/**
 * Get classes unlocked by a user
 */
export async function getUserUnlockedClasses(userId: string): Promise<PlayerClass[]> {
  const { data, error } = await supabase
    .from('player_unlocks')
    .select('unlock_id')
    .eq('user_id', userId)
    .eq('unlock_type', 'class');

  if (error || !data) {
    return [];
  }

  const classIds = data.map((u: { unlock_id: string }) => u.unlock_id);

  const { data: classes } = await supabase
    .from('player_classes')
    .select('*')
    .in('id', classIds);

  return classes || [];
}

// ============================================================================
// Active Quest Operations
// ============================================================================

/**
 * Create a new guild quest
 */
export async function createGuildQuest(
  groupId: string,
  questTemplateId: string,
  partyMembers: string[], // user_ids
  partyClasses: Record<string, string> // user_id -> class_id
): Promise<CreateQuestResponse> {
  try {
    // Get quest template to get start node
    const template = await getQuestTemplate(questTemplateId);
    // Database returns snake_case field names
    const startNodeId = (template as any)?.start_node_id || template?.startNodeId;
    if (!template || !startNodeId) {
      return { success: false, error: 'Invalid quest template' };
    }

    // Validate party size
    const minPlayers = (template as any).min_players || template.minPlayers;
    const maxPlayers = (template as any).max_players || template.maxPlayers;
    if (partyMembers.length < minPlayers || partyMembers.length > maxPlayers) {
      return {
        success: false,
        error: `Party size must be between ${minPlayers} and ${maxPlayers}`
      };
    }

    // Initialize party stats based on selected classes
    const partyStats: Record<string, PlayerStats> = {};
    for (const userId of partyMembers) {
      const classId = partyClasses[userId];
      const { data: classData } = await supabase
        .from('player_classes')
        .select('*')
        .eq('id', classId)
        .single();

      if (!classData) {
        return { success: false, error: `Invalid class for user ${userId}` };
      }

      const baseStats = classData.base_stats as any;
      partyStats[userId] = {
        hp: classData.base_hp,
        maxHp: classData.base_hp,
        atk: baseStats.atk || 0,
        def: baseStats.def || 0,
        str: baseStats.str || 0,
        dex: baseStats.dex || 0,
        int: baseStats.int || 0,
        wis: baseStats.wis || 0,
        cha: baseStats.cha || 0,
      };
    }

    // Randomize turn order
    const turnOrder = [...partyMembers].sort(() => Math.random() - 0.5);

    // Create the quest
    const { data, error } = await supabase
      .from('guild_quests')
      .insert({
        group_id: groupId,
        quest_template_id: questTemplateId,
        current_node_id: startNodeId,
        current_turn_user_id: turnOrder[0],
        turn_started_at: new Date().toISOString(),
        turn_order: turnOrder,
        party_stats: partyStats,
        party_classes: partyClasses,
        status: 'active',
        quest_flags: {},
        inventory: [],
      })
      .select()
      .single();

    if (error) {
      logger.error('Error creating guild quest:', error);
      return { success: false, error: error.message };
    }

    return { success: true, questId: data.id };
  } catch (error: any) {
    logger.error('Error in createGuildQuest:', error);
    return { success: false, error: error.message };
  }
}

/**
 * Get active quests for a group
 */
export async function getGroupQuests(groupId: string): Promise<GuildQuest[]> {
  const { data, error } = await supabase
    .from('guild_quests')
    .select('*')
    .eq('group_id', groupId)
    .in('status', ['active', 'completed'])
    .order('created_at', { ascending: false });

  if (error) {
    logger.error('Error fetching group quests:', error);
    return [];
  }

  return data || [];
}

/**
 * Get a specific guild quest
 */
export async function getGuildQuest(questId: string): Promise<GuildQuest | null> {
  const { data, error } = await supabase
    .from('guild_quests')
    .select('*')
    .eq('id', questId)
    .single();

  if (error) {
    logger.error('Error fetching guild quest:', error);
    return null;
  }

  return data;
}

/**
 * Get quests where it's a user's turn
 */
export async function getUserActiveQuests(userId: string): Promise<GuildQuest[]> {
  const { data, error } = await supabase
    .from('guild_quests')
    .select('*')
    .eq('current_turn_user_id', userId)
    .eq('status', 'active');

  if (error) {
    logger.error('Error fetching user active quests:', error);
    return [];
  }

  return data || [];
}

// ============================================================================
// Quest Progression
// ============================================================================

/**
 * Make a choice in the quest
 */
export async function makeChoice(
  questId: string,
  userId: string,
  choiceId: string
): Promise<MakeChoiceResponse> {
  try {
    // Get the quest
    const quest = await getGuildQuest(questId);
    if (!quest) {
      return { success: false, error: 'Quest not found' };
    }

    // Verify it's the user's turn
    // Database returns snake_case field names
    const currentTurnUserId = (quest as any).current_turn_user_id || quest.currentTurnUserId;
    logger.log('🎯 Turn validation - Quest turn:', currentTurnUserId, 'User ID:', userId);
    if (currentTurnUserId !== userId) {
      return { success: false, error: 'Not your turn' };
    }

    // Get the choice
    const { data: choice } = await supabase
      .from('quest_choices')
      .select('*')
      .eq('id', choiceId)
      .single();

    if (!choice) {
      return { success: false, error: 'Invalid choice' };
    }

    // Check requirements
    const checkResult = await checkChoiceRequirements(quest, userId, choice);
    if (!checkResult.allowed) {
      return { success: false, error: checkResult.reason };
    }

    // Get the next node
    // Database returns snake_case field names
    const leadsToNodeId = (choice as any).leads_to_node_id || choice.leadsToNodeId;
    const nextNode = await getQuestNode(leadsToNodeId);
    if (!nextNode) {
      return { success: false, error: 'Invalid next node' };
    }

    // Log the progress
    const currentNodeId = (quest as any).current_node_id || quest.currentNodeId;
    const choiceText = (choice as any).choice_text || choice.choiceText;
    await supabase.from('quest_progress').insert({
      guild_quest_id: questId,
      node_id: currentNodeId,
      choice_id: choiceId,
      user_id: userId,
      outcome_text: choiceText,
    });

    // Determine next player
    // Database returns snake_case field names
    const turnOrder = ((quest as any).turn_order || quest.turnOrder) as string[];
    const currentIndex = turnOrder.indexOf(userId);
    const nextIndex = (currentIndex + 1) % turnOrder.length;
    const nextUserId = turnOrder[nextIndex];

    // Check if this leads to an ending
    const nodeType = (nextNode as any).node_type || nextNode.nodeType;
    const isEnding = nodeType === 'ending';
    const isCombat = nodeType === 'combat';

    // Update the quest
    const updateData: any = {
      current_node_id: nextNode.id,
      current_turn_user_id: nextUserId,
      turn_started_at: new Date().toISOString(),
    };

    if (isEnding) {
      updateData.status = 'completed';
      updateData.completed_at = new Date().toISOString();
      // Find and set the ending
      const questTemplateId = (quest as any).quest_template_id || quest.questTemplateId;
      const { data: endings } = await supabase
        .from('quest_endings')
        .select('*')
        .eq('quest_template_id', questTemplateId);

      // For now, just mark as completed
      // In a full implementation, we'd match the node to the ending
    }

    if (isCombat) {
      // Initialize combat state (simplified)
      updateData.combat_state = {
        active: true,
        round: 1,
        initiativeOrder: [], // Would be calculated
        currentTurnIndex: 0,
        enemies: [], // Would be loaded from node data
        playerStates: {},
      };
    }

    const { error: updateError } = await supabase
      .from('guild_quests')
      .update(updateData)
      .eq('id', questId);

    if (updateError) {
      return { success: false, error: updateError.message };
    }

    return {
      success: true,
      nextNode,
      combatTriggered: isCombat,
      questCompleted: isEnding,
    };
  } catch (error: any) {
    logger.error('Error in makeChoice:', error);
    return { success: false, error: error.message };
  }
}

/**
 * Check if a user can make a specific choice
 */
async function checkChoiceRequirements(
  quest: GuildQuest,
  userId: string,
  choice: QuestChoice
): Promise<{ allowed: boolean; reason?: string }> {
  // Check class requirement
  if (choice.classRequirement) {
    const partyClasses = (quest as any).party_classes || quest.partyClasses;
    const userClass = (partyClasses as any)[userId];
    const { data: classData } = await supabase
      .from('player_classes')
      .select('name')
      .eq('id', userClass)
      .single();

    if (classData?.name !== choice.classRequirement) {
      return { allowed: false, reason: `Requires ${choice.classRequirement} class` };
    }
  }

  // Check stat requirement
  if (choice.statRequirement) {
    const partyStats = (quest as any).party_stats || quest.partyStats;
    const userStats = (partyStats as any)[userId];
    const { stat, min } = choice.statRequirement as any;
    if (userStats[stat] < min) {
      return { allowed: false, reason: `Requires ${stat} ${min}+` };
    }
  }

  // Check item requirement
  if (choice.itemRequirement) {
    const inventory = quest.inventory as any[];
    const hasItem = inventory.some(item => item.name === choice.itemRequirement);
    if (!hasItem) {
      return { allowed: false, reason: `Requires ${choice.itemRequirement}` };
    }
  }

  // Check knowledge requirement
  if (choice.knowledgeRequirement) {
    const questFlags = (quest as any).quest_flags || quest.questFlags;
    const flags = questFlags as any;
    if (!flags[choice.knowledgeRequirement]) {
      return { allowed: false, reason: 'Requirements not met' };
    }
  }

  return { allowed: true };
}

// ============================================================================
// Combat System
// ============================================================================

/**
 * Perform a combat action
 */
export async function performCombatAction(
  questId: string,
  userId: string,
  action: CombatAction
): Promise<PerformCombatActionResponse> {
  try {
    const quest = await getGuildQuest(questId);
    const combatState = (quest as any)?.combat_state || quest?.combatState;
    if (!quest || !combatState) {
      return { success: false, error: 'No active combat' };
    }

    const combat = combatState as CombatState;

    // Verify it's the user's turn in combat
    const currentTurnId = combat.initiativeOrder[combat.currentTurnIndex];
    if (currentTurnId !== userId) {
      return { success: false, error: 'Not your turn in combat' };
    }

    // Execute the action and get result
    const result = await executeCombatAction(quest, userId, action);

    // Log the combat turn
    await supabase.from('quest_combat_turns').insert({
      guild_quest_id: questId,
      combat_round: combat.round,
      turn_number: combat.currentTurnIndex,
      user_id: userId,
      action_type: action.actionType,
      target: action.targetId,
      dice_roll: result.diceRoll,
      damage_dealt: result.damage,
      result_text: result.message,
    });

    // Update combat state
    combat.currentTurnIndex++;

    // Check if round is over
    if (combat.currentTurnIndex >= combat.initiativeOrder.length) {
      combat.currentTurnIndex = 0;
      combat.round++;
    }

    // Check win/loss conditions
    const combatEnded = checkCombatEnd(combat);
    let victory = false;

    if (combatEnded) {
      victory = combat.enemies.every(e => e.hp <= 0);
      combat.active = false;
    }

    // Update quest
    await supabase
      .from('guild_quests')
      .update({ combat_state: combat })
      .eq('id', questId);

    return {
      success: true,
      result,
      combatEnded,
      victory,
    };
  } catch (error: any) {
    logger.error('Error in performCombatAction:', error);
    return { success: false, error: error.message };
  }
}

/**
 * Execute a combat action and return the result
 */
async function executeCombatAction(
  quest: GuildQuest,
  userId: string,
  action: CombatAction
): Promise<CombatResult> {
  const partyStats = (quest as any).party_stats || quest.partyStats;
  const userStats = (partyStats as any)[userId] as PlayerStats;
  const combatState = (quest as any).combat_state || quest.combatState;
  const combat = combatState as CombatState;

  // Simplified combat resolution
  const diceRoll = Math.floor(Math.random() * 20) + 1;
  const isCritical = diceRoll === 20;
  const isCriticalFail = diceRoll === 1;

  let damage = 0;
  let hit = false;
  let message = '';

  if (action.actionType === 'attack') {
    const attackRoll = diceRoll + userStats.atk;
    const target = combat.enemies.find(e => e.id === action.targetId);

    if (!target) {
      return {
        success: false,
        hit: false,
        damage: 0,
        isCritical: false,
        isCriticalFail: false,
        message: 'Invalid target',
      };
    }

    hit = attackRoll >= target.def || isCritical;

    if (hit && !isCriticalFail) {
      damage = Math.floor(Math.random() * 8) + 1 + userStats.str;
      if (isCritical) {
        damage *= 2;
      }
      target.hp -= damage;
      message = `You hit ${target.name} for ${damage} damage!${isCritical ? ' CRITICAL!' : ''}`;
    } else {
      message = `Your attack missed ${target.name}!${isCriticalFail ? ' CRITICAL FAIL!' : ''}`;
    }
  } else if (action.actionType === 'defend') {
    message = 'You take a defensive stance, gaining +3 DEF until your next turn.';
    // Would apply buff here
  }

  return {
    success: true,
    diceRoll,
    hit,
    damage,
    isCritical,
    isCriticalFail,
    message,
  };
}

/**
 * Check if combat has ended
 */
function checkCombatEnd(combat: CombatState): boolean {
  // All enemies dead
  const allEnemiesDead = combat.enemies.every(e => e.hp <= 0);

  // All players dead (would need to check player states)
  // Simplified for now

  return allEnemiesDead;
}

/**
 * Calculate initiative order for combat
 */
export function calculateInitiative(
  partyMembers: string[],
  partyClasses: Record<string, string>,
  enemies: Enemy[]
): string[] {
  const initiatives: { id: string; score: number }[] = [];

  // Player initiatives
  for (const userId of partyMembers) {
    const classId = partyClasses[userId];
    // Simplified - would need to look up class for base initiative
    const baseInit = 10;
    const roll = Math.floor(Math.random() * 6) + 1;
    initiatives.push({ id: userId, score: baseInit + roll });
  }

  // Enemy initiatives
  for (const enemy of enemies) {
    const roll = Math.floor(Math.random() * 6) + 1;
    initiatives.push({ id: enemy.id, score: 10 + roll });
  }

  // Sort by initiative (highest first)
  initiatives.sort((a, b) => b.score - a.score);

  return initiatives.map(i => i.id);
}

/**
 * Get quest progress log
 */
export async function getQuestProgress(questId: string): Promise<QuestProgress[]> {
  const { data, error } = await supabase
    .from('quest_progress')
    .select('*')
    .eq('guild_quest_id', questId)
    .order('created_at', { ascending: true });

  if (error) {
    logger.error('Error fetching quest progress:', error);
    return [];
  }

  return data || [];
}

/**
 * Check for turn timeouts
 */
export async function checkTurnTimeout(questId: string): Promise<boolean> {
  const quest = await getGuildQuest(questId);
  if (!quest || quest.status !== 'active') {
    return false;
  }

  const turnStartedAt = (quest as any).turn_started_at || quest.turnStartedAt;
  const turnStarted = new Date(turnStartedAt!).getTime();
  const now = Date.now();
  const elapsed = now - turnStarted;

  if (elapsed >= TURN_TIMEOUT_MS) {
    // Timeout occurred
    const turnOrder = ((quest as any).turn_order || quest.turnOrder) as string[];
    const currentTurnUserId = (quest as any).current_turn_user_id || quest.currentTurnUserId;
    const currentIndex = turnOrder.indexOf(currentTurnUserId!);
    const nextIndex = (currentIndex + 1) % turnOrder.length;
    const nextUserId = turnOrder[nextIndex];

    // Log the timeout
    const currentNodeId = (quest as any).current_node_id || quest.currentNodeId;
    await supabase.from('quest_turn_timeouts').insert({
      guild_quest_id: questId,
      user_id: currentTurnUserId,
      node_id: currentNodeId,
      next_player_user_id: nextUserId,
    });

    // Skip to next player
    await supabase
      .from('guild_quests')
      .update({
        current_turn_user_id: nextUserId,
        turn_started_at: new Date().toISOString(),
      })
      .eq('id', questId);

    return true;
  }

  return false;
}
